import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

export default function LandingPage({ topMovies, user }) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener("resize", checkScrollPosition);
    return () => window.removeEventListener("resize", checkScrollPosition);
  }, []);

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>TollyHype – Tollywood Box Office, Hype Rankings & Movie Data</title>
        <meta name="description" content="Win free movie ticket, Weekly box office reports, hype rankings, upcoming movies & Tollywood insights." />
        <link rel="canonical" href="https://tollyhype.com" />
        <meta property="og:title" content="TollyHype" />
        <meta property="og:description" content="Tollywood box office & hype rankings updated weekly." />
        <meta property="og:image" content="/logo512.png" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">TollyHype</h1>
          <p className="hero-subtitle">
            Discover, vote, and celebrate your favorite Telugu movies
          </p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => navigate(user ? "/movies" : "/login")}>
              {user ? "Explore Movies" : "Get Started"}
            </button>
            <button className="btn-secondary" onClick={() => navigate("/boxoffice")}>
              View Box Office
            </button>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-emoji">🌟</div>
        </div>
      </section>

      {/* Top Movies Carousel */}
      <section className="carousel-section">
        <div className="carousel-header">
          <h2>🏆 Top 5 Movies Right Now</h2>
          <p className="carousel-subtitle">Most hyped movies by our community</p>
        </div>

        <div className="carousel-wrapper">
          {canScrollLeft && (
            <button className="carousel-arrow carousel-arrow-left" onClick={() => scroll("left")}>
              ← 
            </button>
          )}
          
          <div className="carousel-container" ref={scrollContainerRef} onScroll={checkScrollPosition}>
            {topMovies.length > 0 ? (
              topMovies.map((movie, idx) => (
                <div key={movie.id} className="carousel-item">
                  <div className="carousel-rank">#{idx + 1}</div>
                  <div className="carousel-poster">
                    {movie.posterUrl ? (
                      <img 
                        src={movie.posterUrl} 
                        alt={movie.title} 
                        loading="lazy"
                        width="300"
                        height="450"
                        decoding="async"
                        fetchPriority="high"
                      />
                    ) : (
                      <div className="poster-placeholder" style={{ width: '300px', height: '450px' }}>No image</div>
                    )}
                  </div>
                  <div className="carousel-info">
                    <h3 className="carousel-title">{movie.title}</h3>
                    <div className="carousel-points">
                      <span className="points-badge">⭐ {movie.totalPoints} points</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="carousel-empty">No movies yet</div>
            )}
          </div>

          {canScrollRight && (
            <button className="carousel-arrow carousel-arrow-right" onClick={() => scroll("right")}>
              →
            </button>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2>Why You'll Love It</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>Vote for Your Favorites</h3>
            <p>Cast your vote daily and support the movies you love</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Live Rankings</h3>
            <p>See real-time rankings of the most hyped movies</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎬</div>
            <h3>Box Office Stats</h3>
            <p>Check box office collections and yearly performance</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🌟</div>
            <h3>Community Powered</h3>
            <p>Join thousands of Telugu movie enthusiasts</p>
          </div>
        </div>
      </section>

      {/* Rewards Section - SEO Optimized */}
      <section className="rewards-section">
        <div className="rewards-content">
          <h2>🎁 Win Free Movie Tickets & Exclusive Rewards!</h2>
          <p className="rewards-lead">
            Join thousands of Telugu movie fans and unlock amazing rewards just by voting for your favorite films!
          </p>
          
          <div className="rewards-grid">
            <div className="reward-highlight">
              <div className="reward-icon-large">🎫</div>
              <h3>Free Movie Ticket at Level 10</h3>
              <p>
                Reach Level 10 by voting for movies and unlock a <strong>FREE movie ticket worth ₹250</strong>! 
                Our team will contact you via email within 1-2 days to deliver your reward. 
                Perfect for catching the latest Telugu blockbusters!
              </p>
            </div>
            
            <div className="reward-highlight">
              <div className="reward-icon-large">⭐</div>
              <h3>Bonus Points at Every Milestone</h3>
              <p>
                Earn <strong>50 bonus points at Level 2</strong> and <strong>250 bonus points at Level 5</strong>! 
                Use these points immediately to vote for more movies and climb the leaderboard. 
                The more you vote, the more rewards you unlock!
              </p>
            </div>
            
            <div className="reward-highlight">
              <div className="reward-icon-large">🏆</div>
              <h3>Monthly Leaderboard Winner</h3>
              <p>
                <strong>Win a free movie ticket worth ₹250 every month!</strong> Top the leaderboard and claim your prize. 
                Compete with other Telugu movie fans, vote daily, and climb to the #1 spot to win big!
              </p>
            </div>
          </div>

          <div className="rewards-cta">
            <p className="rewards-cta-text">
              <strong>Ready to start earning rewards?</strong> Sign up now and begin your journey to free movie tickets!
            </p>
            <button className="btn-primary btn-large" onClick={() => navigate(user ? "/movies" : "/register")}>
              {user ? "Start Voting Now" : "Sign Up & Win Rewards"}
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Join the Hype?</h2>
        <p>Start voting, discover trending movies, and be part of our growing community</p>
        <button className="btn-large" onClick={() => navigate(user ? "/movies" : "/register")}>
          {user ? "Start Exploring" : "Sign Up Now"}
        </button>
      </section>

    </div>
  );
}
